import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MagnifyingGlass, ArrowsClockwise, Percent, Upload, PencilLine, Tag } from "@phosphor-icons/react";

const HEAD_ORDER = ["VRV", "Light Commercial", "Room Air Conditioners"];
const headSlug = (h) => h.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const headOf = (x) => x.group || "VRV";

const tabCls = (active) =>
  `px-3 py-1.5 rounded-sm text-[11.5px] font-semibold border transition-colors ${
    active
      ? "bg-[#0F172A] text-white border-[#0F172A]"
      : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
  }`;

/**
 * Price Catalog — Master price list for Sales module.
 * Organised under three heads: VRV · Light Commercial (Sky Air / PA) ·
 * Room Air Conditioners. Supports search, category filter, per-SKU edit,
 * bulk discount, and Excel re-import.
 */
export default function PriceCatalog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [head, setHead] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDisc, setBulkDisc] = useState(30);
  const [bulkCat, setBulkCat] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [{ data: r }, { data: c }] = await Promise.all([
        api.get("/price-list", { params: { q, category: cat, limit: 1000 } }),
        api.get("/price-list/categories"),
      ]);
      setRows(r || []);
      setCats(c || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }, [q, cat]);

  useEffect(() => { load(); }, [load]);

  const seedIfEmpty = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/price-list/seed", null, { params: { replace: false } });
      toast.success(`Seeded ${data.inserted} SKUs · ${data.total_daikin} total`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const applyBulk = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/price-list/bulk-discount", { discount_pct: bulkDisc, category: bulkCat });
      toast.success(`Updated ${data.updated} SKUs → ${bulkDisc}% discount`);
      setBulkOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const onExcelUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/price-list/import-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import: ${data.inserted} new · ${data.updated} updated · total ${data.total}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const heads = useMemo(() => {
    const m = {};
    cats.forEach((c) => { const g = headOf(c); m[g] = (m[g] || 0) + c.count; });
    return HEAD_ORDER.filter((h) => m[h]).map((h) => ({ name: h, count: m[h] }));
  }, [cats]);

  const visibleRows = useMemo(
    () => (head ? rows.filter((r) => headOf(r) === head) : rows),
    [rows, head]
  );

  const groupedRows = useMemo(() => {
    const order = head ? [head] : HEAD_ORDER;
    const map = {};
    visibleRows.forEach((r) => {
      const g = headOf(r);
      (map[g] = map[g] || []).push(r);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) => (a.category || "").localeCompare(b.category || "") || (a.model || "").localeCompare(b.model || ""))
    );
    return order.filter((g) => map[g]?.length).map((g) => [g, map[g]]);
  }, [visibleRows, head]);

  const totals = useMemo(() => {
    const list = visibleRows.reduce((s, r) => s + (r.daikin_list_price || 0), 0);
    const cel = visibleRows.reduce((s, r) => s + (r.celsius_price || 0), 0);
    const avgDiscount = visibleRows.length ? visibleRows.reduce((s, r) => s + (r.discount_pct || 0), 0) / visibleRows.length : 0;
    return { list, cel, avgDiscount };
  }, [visibleRows]);

  // Category options filtered by the selected head — memoized to skip
  // recomputing on every keystroke in the search box.
  const catOptions = useMemo(
    () => cats.filter((c) => !head || headOf(c) === head),
    [cats, head]
  );

  const pickHead = (h) => { setHead(h); setCat(""); };

  return (
    <div className="space-y-4 p-4" data-testid="price-catalog-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold font-display text-slate-900 tracking-tight">Price Catalog</h1>
          <div className="text-[11.5px] text-slate-500">Daikin master price list · {visibleRows.length} SKUs shown</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-sm text-[11.5px] h-8" onClick={seedIfEmpty} data-testid="seed-daikin-btn">
            <Tag size={12} className="mr-1" /> Seed Daikin
          </Button>
          <label className="inline-flex" data-testid="upload-price-excel-wrap">
            <input type="file" accept=".xlsx" onChange={onExcelUpload} className="hidden" data-testid="upload-price-excel" />
            <Button asChild variant="outline" className="rounded-sm text-[11.5px] h-8">
              <span><Upload size={12} className="mr-1" /> Import Excel</span>
            </Button>
          </label>
          <Button
            onClick={() => setBulkOpen(true)}
            className="rounded-sm text-[11.5px] h-8 bg-[#EA580C] hover:bg-[#c2410c] text-white"
            data-testid="bulk-discount-btn"
          >
            <Percent size={12} className="mr-1" /> Bulk Discount
          </Button>
        </div>
      </div>

      {/* Totals band */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="catalog-totals">
        <StatTile label="SKUs Shown" value={visibleRows.length} tone="slate" />
        <StatTile label="Categories" value={cats.length} tone="slate" />
        <StatTile label="Sum Daikin List" value={fmtMoney(totals.list)} tone="dark" />
        <StatTile label="Sum Celsius Price" value={fmtMoney(totals.cel)} tone="orange" />
      </div>

      {/* Head tabs */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="price-head-tabs">
        <button onClick={() => pickHead("")} className={tabCls(head === "")} data-testid="price-head-tab-all">
          All <span className="ml-1 opacity-60 font-mono-num">{rows.length}</span>
        </button>
        {heads.map((h) => (
          <button
            key={h.name}
            onClick={() => pickHead(h.name)}
            className={tabCls(head === h.name)}
            data-testid={`price-head-tab-${headSlug(h.name)}`}
          >
            {h.name} <span className="ml-1 opacity-60 font-mono-num">{h.count}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-sm px-3 py-2">
        <MagnifyingGlass size={14} className="text-slate-400" />
        <Input
          placeholder="Search model or description…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-8 text-[12px] w-72"
          data-testid="catalog-search"
        />
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="border border-slate-200 rounded-sm h-8 text-[11.5px] px-2"
          data-testid="catalog-cat-filter"
        >
          <option value="">All categories</option>
          {catOptions.map((c) => <option key={c.category} value={c.category}>{c.category} ({c.count})</option>)}
        </select>
        <Button variant="ghost" size="sm" className="h-8 text-[11.5px]" onClick={load} data-testid="catalog-refresh">
          <ArrowsClockwise size={12} className="mr-1" /> Refresh
        </Button>
        {(q || cat || head) && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-[11.5px] text-red-600"
            onClick={() => { setQ(""); setCat(""); setHead(""); }}
            data-testid="catalog-clear"
          >
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="text-[11.5px]">
              <TableHeader>
                <TableRow className="bg-[#0F172A]">
                  <TableHead className="text-white/90 w-40">Category</TableHead>
                  <TableHead className="text-white/90 w-32">Model</TableHead>
                  <TableHead className="text-white/90">Description</TableHead>
                  <TableHead className="text-white/90 text-right w-28">Daikin List ₹</TableHead>
                  <TableHead className="text-white/90 text-right w-20">Disc %</TableHead>
                  <TableHead className="text-white/90 text-right w-28">Celsius ₹</TableHead>
                  <TableHead className="text-white/90 w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRows.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">
                    {busy ? "Loading…" : "No SKUs found. Clear the filters or click 'Seed Daikin' to import the master price list."}
                  </TableCell></TableRow>
                )}
                {groupedRows.map(([gname, grows]) => (
                  <React.Fragment key={gname}>
                    <TableRow className="bg-slate-100 hover:bg-slate-100" data-testid={`price-group-header-${headSlug(gname)}`}>
                      <TableCell colSpan={7} className="py-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-600">{gname}</span>
                        <span className="ml-2 text-[10px] text-slate-400 font-mono-num">{grows.length} SKUs</span>
                      </TableCell>
                    </TableRow>
                    {grows.map((r) => (
                      <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`sku-row-${r.model}`}>
                        <TableCell className="text-[11px] text-slate-600">{r.category}</TableCell>
                        <TableCell className="font-mono font-semibold text-slate-900">
                          {r.model}
                          {r.star_rating && (
                            <span className="ml-1.5 inline-block px-1 py-0.5 rounded-sm text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300" data-testid={`sku-star-${r.model}`}>
                              ★ {r.star_rating.replace(" Star", "")} Star
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-[11px] text-slate-700">{r.description}</TableCell>
                        <TableCell className="text-right font-mono-num">{fmtMoney(r.daikin_list_price)}</TableCell>
                        <TableCell className="text-right font-mono-num">
                          <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10.5px] font-semibold ${(r.discount_pct || 0) > 0 ? "bg-emerald-50 text-emerald-700" : "bg-slate-50 text-slate-500"}`}>
                            {(r.discount_pct || 0).toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono-num font-bold text-[#EA580C]">
                          {fmtMoney(r.celsius_price)}
                          {isAdmin && r.dbp_price > 0 && (
                            <div className="text-[9px] font-normal text-slate-400 mt-0.5" data-testid={`sku-margin-${r.model}`} title="Dealer billing price (cost) vs your selling price">
                              cost {fmtMoney(r.dbp_price)} · M {((r.celsius_price - r.dbp_price) / r.celsius_price * 100).toFixed(1)}%
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={() => setEditing(r)}
                            data-testid={`sku-edit-${r.model}`}
                          >
                            <PencilLine size={12} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {editing && <SkuEditDialog sku={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-discount-dialog">
          <DialogHeader><DialogTitle className="font-display">Bulk Discount</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-widest">Category</label>
              <select
                value={bulkCat}
                onChange={(e) => setBulkCat(e.target.value)}
                className="w-full border border-slate-200 rounded-sm h-9 text-[12.5px] px-2 mt-1"
                data-testid="bulk-cat"
              >
                <option value="">All categories ({rows.length} SKUs)</option>
                {cats.map((c) => <option key={c.category} value={c.category}>{c.category} ({c.count})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-widest">Discount %</label>
              <Input
                type="number"
                min={0}
                max={90}
                step={0.5}
                value={bulkDisc}
                onChange={(e) => setBulkDisc(parseFloat(e.target.value) || 0)}
                className="h-9 mt-1"
                data-testid="bulk-disc-pct"
              />
              <div className="text-[10.5px] text-slate-500 mt-1">Sets discount on every matching SKU and recomputes Celsius price</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={applyBulk} disabled={busy} className="bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="bulk-apply">
              Apply {bulkDisc}% discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatTile({ label, value, tone }) {
  const cls = {
    slate: "bg-white border-slate-200 text-slate-900",
    orange: "bg-[#EA580C] text-white border-[#EA580C]",
    dark: "bg-[#0F172A] text-white border-[#0F172A]",
  }[tone] || "bg-white border-slate-200";
  return (
    <div className={`border ${cls} rounded-sm px-3 py-2`}>
      <div className={`text-[9.5px] uppercase tracking-[0.14em] font-semibold ${tone === "slate" ? "text-slate-500" : "text-white/70"}`}>{label}</div>
      <div className="text-[15px] font-bold font-mono-num mt-0.5">{value}</div>
    </div>
  );
}

function SkuEditDialog({ sku, onClose, onSaved }) {
  const [form, setForm] = useState({
    category: sku.category,
    model: sku.model,
    description: sku.description || "",
    star_rating: sku.star_rating || "",
    daikin_list_price: sku.daikin_list_price,
    discount_pct: sku.discount_pct,
    celsius_price: sku.celsius_price,
    unit: sku.unit || "Nos",
    hsn: sku.hsn || "",
    active: sku.active !== false,
  });

  const save = async () => {
    try {
      await api.put(`/price-list/${sku.id}`, form);
      toast.success("Updated");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  // Auto-recalculate celsius_price on discount/list changes
  const patch = (k, v) => {
    const p = { ...form, [k]: v };
    if (k === "daikin_list_price" || k === "discount_pct") {
      const list = parseFloat(k === "daikin_list_price" ? v : form.daikin_list_price) || 0;
      const disc = parseFloat(k === "discount_pct" ? v : form.discount_pct) || 0;
      p.celsius_price = +(list * (1 - disc / 100)).toFixed(2);
    }
    setForm(p);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="sku-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit · {sku.model}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Row label="Category">
            <Input value={form.category} onChange={(e) => patch("category", e.target.value)} className="h-8" />
          </Row>
          <Row label="Model">
            <Input value={form.model} onChange={(e) => patch("model", e.target.value)} className="h-8" />
          </Row>
          <Row label="Description">
            <Input value={form.description} onChange={(e) => patch("description", e.target.value)} className="h-8" />
          </Row>
          <Row label="Star Rating">
            <select
              value={form.star_rating}
              onChange={(e) => patch("star_rating", e.target.value)}
              className="w-full border border-slate-200 rounded-sm h-8 text-[12px] px-2"
              data-testid="sku-star-rating"
            >
              <option value="">— none —</option>
              {["1 Star", "2 Star", "3 Star", "4 Star", "5 Star"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Row>
          <div className="grid grid-cols-3 gap-2">
            <Row label="Daikin List ₹">
              <Input type="number" value={form.daikin_list_price} onChange={(e) => patch("daikin_list_price", e.target.value)} className="h-8" data-testid="sku-list-price" />
            </Row>
            <Row label="Discount %">
              <Input type="number" value={form.discount_pct} onChange={(e) => patch("discount_pct", e.target.value)} className="h-8" data-testid="sku-discount-pct" />
            </Row>
            <Row label="Celsius ₹">
              <Input type="number" value={form.celsius_price} onChange={(e) => patch("celsius_price", e.target.value)} className="h-8" data-testid="sku-celsius-price" />
            </Row>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="sku-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
